import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2, ExternalLink, RefreshCw, ChevronDown, Play, Tag, Volume2, VolumeX, Flame } from "lucide-react";
import { fetchFeed, fetchFeedGenres } from "@/lib/api.ts";
import type { FeedItem } from "@/lib/types.ts";

const ITEMS_PER_LOAD = 10;

export function FeedPage() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // ジャンルフィルター state
  const [genres, setGenres] = useState<{ name: string; count: number }[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [onSale, setOnSale] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // ジャンル一覧取得
  useEffect(() => {
    fetchFeedGenres().then(setGenres).catch(() => {});
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute("data-index"));
            if (!isNaN(idx)) setActiveIndex(idx);
          }
        }
      },
      { root: container, threshold: 0.6 }
    );

    cardRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFeed(ITEMS_PER_LOAD, selectedGenre ?? undefined, onSale || undefined);
      setItems(result.data);
      setActiveIndex(0);
      containerRef.current?.scrollTo({ top: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedGenre, onSale]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await fetchFeed(ITEMS_PER_LOAD, selectedGenre ?? undefined, onSale || undefined);
      setItems((prev) => [...prev, ...result.data]);
    } finally {
      setLoadingMore(false);
    }
  };

  const lastCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || loadingMore) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) {
            loadMore();
            observer.disconnect();
          }
        },
        { root: containerRef.current, threshold: 0.5 }
      );
      observer.observe(node);
    },
    [loadingMore, items.length]
  );

  const setCardRef = useCallback(
    (index: number) => (node: HTMLDivElement | null) => {
      if (node) cardRefs.current.set(index, node);
      else cardRefs.current.delete(index);
    },
    []
  );

  const handleGenreSelect = (genre: string | null) => {
    setSelectedGenre(genre);
    setOnSale(false);
  };

  const handleSaleToggle = () => {
    setOnSale(true);
    setSelectedGenre(null);
  };

  const handleGenreTagClick = (genre: string) => {
    setSelectedGenre(genre);
    setOnSale(false);
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center flex-1">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <>
        <GenreBar
          genres={genres}
          selectedGenre={selectedGenre}
          onSale={onSale}
          onGenreSelect={handleGenreSelect}
          onSaleToggle={handleSaleToggle}
        />
        <div className="flex flex-col items-center justify-center flex-1 gap-4 pt-20">
          <p className="text-muted-foreground font-[var(--font-secondary)]">
            {onSale ? "セール中の動画はありません" : selectedGenre ? `「${selectedGenre}」の動画はありません` : "サンプル動画がまだありません"}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <GenreBar
        genres={genres}
        selectedGenre={selectedGenre}
        onSale={onSale}
        onGenreSelect={handleGenreSelect}
        onSaleToggle={handleSaleToggle}
      />
      <div
        ref={containerRef}
        className="fixed top-[112px] left-0 right-0 bottom-0 overflow-y-auto overscroll-none"
        style={{ scrollSnapType: "y mandatory" }}
      >
        {items.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            ref={(node) => {
              setCardRef(index)(node);
              if (index === items.length - 1 && node) lastCardRef(node);
            }}
            data-index={index}
            className="w-full shrink-0"
            style={{
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
              height: "calc(100dvh - 112px)",
            }}
          >
            <FeedCard
              item={item}
              isActive={Math.abs(activeIndex - index) <= 1}
              isCurrent={activeIndex === index}
              isFirst={index === 0}
              isMuted={isMuted}
              onToggleMute={() => setIsMuted((m) => !m)}
              onGenreTagClick={handleGenreTagClick}
            />
          </div>
        ))}
      </div>

      <button
        onClick={async () => {
          setLoading(true);
          try {
            const result = await fetchFeed(ITEMS_PER_LOAD, selectedGenre ?? undefined, onSale || undefined);
            setItems(result.data);
            setActiveIndex(0);
            containerRef.current?.scrollTo({ top: 0 });
          } finally {
            setLoading(false);
          }
        }}
        className="fixed bottom-6 right-6 z-20 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium bg-card/90 text-foreground border border-border shadow-lg backdrop-blur cursor-pointer hover:bg-secondary transition-colors"
      >
        <RefreshCw size={14} />
        シャッフル
      </button>
    </>
  );
}

function GenreBar({
  genres,
  selectedGenre,
  onSale,
  onGenreSelect,
  onSaleToggle,
}: {
  genres: { name: string; count: number }[];
  selectedGenre: string | null;
  onSale: boolean;
  onGenreSelect: (genre: string | null) => void;
  onSaleToggle: () => void;
}) {
  return (
    <div className="fixed top-[72px] left-0 right-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="flex gap-2 px-4 py-2 overflow-x-auto no-scrollbar">
        <button
          onClick={() => { onGenreSelect(null); }}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${
            !selectedGenre && !onSale
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground hover:bg-muted"
          }`}
        >
          すべて
        </button>
        <button
          onClick={onSaleToggle}
          className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors flex items-center gap-1 ${
            onSale
              ? "bg-red-500 text-white"
              : "bg-secondary text-secondary-foreground hover:bg-muted"
          }`}
        >
          <Flame size={12} />
          セール中
        </button>
        {genres.map((g) => (
          <button
            key={g.name}
            onClick={() => onGenreSelect(g.name)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${
              selectedGenre === g.name
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-muted"
            }`}
          >
            {g.name}
          </button>
        ))}
      </div>
    </div>
  );
}

function FeedCard({
  item,
  isActive,
  isCurrent,
  isFirst,
  isMuted,
  onToggleMute,
  onGenreTagClick,
}: {
  item: FeedItem;
  isActive: boolean;
  isCurrent: boolean;
  isFirst: boolean;
  isMuted: boolean;
  onToggleMute: () => void;
  onGenreTagClick: (genre: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(false);

  const isOnSale = item.campaign != null && item.campaign.end > new Date().toISOString().replace("T", " ");

  // ミュート状態が変わったらvideoに反映
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = isMuted;
  }, [isMuted]);

  // カード自身の可視性を直接監視して再生/停止（親のactiveIndex待ちより即座に反応）
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting ?? false;
        isVisibleRef.current = visible;
        const video = videoRef.current;
        if (!video) return;
        if (visible) {
          video.currentTime = 0;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, [item.videoUrl]);

  return (
    <div ref={cardRef} className="h-full flex flex-col bg-background relative">
      {/* 動画 - 16:9 */}
      <div className="w-full relative bg-black shrink-0" style={{ paddingBottom: "56.25%" }}>
        {isActive && item.videoUrl ? (
          <>
            <video
              ref={videoRef}
              src={item.videoUrl}
              className="absolute inset-0 w-full h-full object-contain"
              muted={isMuted}
              playsInline
              controls
              preload={isCurrent ? "auto" : "metadata"}
            />
            {/* ミュートトグルボタン */}
            <button
              onClick={onToggleMute}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm border-0 cursor-pointer hover:bg-black/70 transition-colors"
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </>
        ) : isActive ? (
          <iframe
            src={item.sampleUrl}
            className="absolute inset-0 w-full h-full border-0"
            allowFullScreen
            allow="autoplay; encrypted-media"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {item.imageUrl ? (
              <>
                <img src={item.imageUrl} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40" />
              </>
            ) : null}
            <Play size={48} className="relative text-white/70" />
          </div>
        )}
      </div>

      {/* 情報エリア */}
      <div className="flex-1 min-h-0 flex flex-col bg-card overflow-y-auto">
        <div className="px-4 pt-3 pb-2 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-[var(--font-primary)] text-[15px] font-semibold text-foreground m-0 leading-snug flex-1 line-clamp-2">
              {item.title}
            </h3>
            <div className="shrink-0 flex flex-col items-end gap-0.5">
              {isOnSale && item.listPrice != null && item.minPrice != null && item.listPrice > item.minPrice ? (
                <>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500 text-white text-xs font-bold">
                    SALE ¥{item.minPrice.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-muted-foreground line-through">
                    ¥{item.listPrice.toLocaleString()}
                  </span>
                </>
              ) : item.minPrice != null ? (
                <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-bold whitespace-nowrap">
                  ¥{item.minPrice.toLocaleString()}~
                </span>
              ) : null}
            </div>
          </div>

          {/* キャンペーン名 */}
          {isOnSale && item.campaign && (
            <span className="text-[11px] text-red-500 font-medium">
              {item.campaign.title}
            </span>
          )}

          {item.genres.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {item.genres.slice(0, 5).map((genre) => (
                <button
                  key={genre}
                  onClick={() => onGenreTagClick(genre)}
                  className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground text-[11px] border-0 cursor-pointer hover:bg-muted transition-colors"
                >
                  <Tag size={10} className="shrink-0" />
                  {genre}
                </button>
              ))}
            </div>
          )}

          {item.date && (
            <span className="font-[var(--font-secondary)] text-[11px] text-muted-foreground">
              {item.date}
            </span>
          )}
        </div>

        <div className="px-4 py-2 border-t border-border">
          <Link
            to={`/actress/${item.actress.id}`}
            className="flex items-center gap-3 no-underline group"
          >
            {item.actress.imageUrl ? (
              <img src={item.actress.imageUrl} alt={item.actress.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-sm text-muted-foreground shrink-0">
                {item.actress.name?.[0]}
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="font-[var(--font-primary)] text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                {item.actress.name}
              </span>
              {item.actress.cupSize && (
                <span className="text-[11px] text-muted-foreground">{item.actress.cupSize}カップ</span>
              )}
            </div>
          </Link>
        </div>

        {item.similarActresses.length > 0 && (
          <div className="px-4 py-2 border-t border-border">
            <p className="font-[var(--font-secondary)] text-[11px] text-muted-foreground m-0 mb-2">
              体型が近い女優
            </p>
            <div className="flex gap-3">
              {item.similarActresses.map((sa) => (
                <Link
                  key={sa.id}
                  to={`/actress/${sa.id}`}
                  className="flex flex-col items-center gap-1 no-underline group w-16"
                >
                  {sa.imageUrl ? (
                    <img src={sa.imageUrl} alt={sa.name} className="w-12 h-12 rounded-full object-cover group-hover:ring-2 ring-primary transition-all" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-sm text-muted-foreground">
                      {sa.name?.[0]}
                    </div>
                  )}
                  <span className="font-[var(--font-secondary)] text-[11px] text-foreground group-hover:text-primary transition-colors text-center truncate w-full">
                    {sa.name}
                  </span>
                  {sa.cupSize && (
                    <span className="text-[10px] text-muted-foreground leading-none">{sa.cupSize}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1" />
        {item.affiliateUrl && (
          <div className="px-4 py-3 border-t border-border">
            <a
              href={item.affiliateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold bg-primary text-primary-foreground no-underline hover:opacity-90 transition-opacity"
            >
              <ExternalLink size={14} />
              FANZAで見る
              {item.minPrice != null && (
                <span className="font-normal text-primary-foreground/80">
                  ¥{item.minPrice.toLocaleString()}~
                </span>
              )}
            </a>
          </div>
        )}
      </div>

      {isFirst && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce text-muted-foreground pointer-events-none">
          <ChevronDown size={24} />
        </div>
      )}
    </div>
  );
}
